import { useEffect } from 'react';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { useSessionData } from '@confluence/session-data';
import { expVal, expValEquals } from '@confluence/feature-experiments';

import { useFireEventOnce } from '../use-fire-event-once';
import { useRunMsrNudges } from '../../services/msr-cross-join-nudge';

// TODO: Remove export of this hook once the CrossJoinNudgesWithFlags is fully rolled out
export const useCrossJoinNudges = ({
	flags,
	execNudge = false,
}: {
	flags: FlagsStateContainer;
	execNudge: boolean;
}) => {
	const { formatMessage } = useIntl();
	const { cloudId, userId, locale } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireAnalyticEventOnceForMsr = useFireEventOnce();
	const fireAnalyticEventOnceForLoom = useFireEventOnce();
	const { push } = useRouteActions();
	const runMsrNudges = useRunMsrNudges({ cloudId, userId, flags, createAnalyticsEvent, push });

	useEffect(() => {
		const execNudges = async () => {
			const {
				checkJpdCrossJoinNudgeEligibility,
				showJpdCrossJoinNudge,
				triggerJiraCrossJoinNudge,
			} = await import(
				/* webpackChunkName: "loadable-cross-join-nudges-services" */ '../../services/run-cross-join-nudges'
			);

			const isJpdCrossJoinNudgesEligible = await checkJpdCrossJoinNudgeEligibility({
				cloudId,
				userId,
				createAnalyticsEvent,
			});

			isJpdCrossJoinNudgesEligible
				? await showJpdCrossJoinNudge({ cloudId, userId, flags, createAnalyticsEvent, push })
				: await triggerJiraCrossJoinNudge({ cloudId, userId, flags, createAnalyticsEvent, push });
		};

		const execNudgesWithLoom = async () => {
			if (
				!userId ||
				!(locale || '').toLowerCase().startsWith('en') ||
				!fg('twcg-443-cjn-confluence-loom-killswitch')
			) {
				await execNudges();
				return;
			}

			const { checkLoomCrossJoinNudgeEligibility, showLoomCrossJoinNudge } = await import(
				/* webpackChunkName: "loadable-loom-cross-join-nudges-services" */ '../../services/loom-cross-join-nudge'
			);

			const isLoomCrossJoinNudgesEligible = await checkLoomCrossJoinNudgeEligibility({
				cloudId,
				userId,
				createAnalyticsEvent,
			});

			if (isLoomCrossJoinNudgesEligible.eligible) {
				if (expVal('twcg-443-cjn-confluence-loom', 'cohort', 'not-enrolled') !== 'not-enrolled') {
					fireAnalyticEventOnceForLoom(
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'exposed',
								actionSubject: 'confluenceToLoomNudgeFeature',
								source: 'crossJoinNudges',
								attributes: {
									flagKey: 'twcg-443-cjn-confluence-loom',
									cohort: expVal('twcg-443-cjn-confluence-loom', 'cohort', 'not-enrolled'),
								},
							},
						}),
					);
				}
				await showLoomCrossJoinNudge({
					cloudId,
					userId,
					flags,
					createAnalyticsEvent,
					formatMessage,
				});
			} else {
				await execNudges();
			}
		};

		const orchestrateNudges = async () => {
			const { checkMsrCrossJoinNudgeEligibility, setEnrolledInMsr } = await import(
				/* webpackChunkName: "loadable-msr-cross-join-nudges-services" */ '../../services/msr-cross-join-nudge'
			);

			if (await checkMsrCrossJoinNudgeEligibility()) {
				if (expValEquals('twcg-437-cjn-msr-m1', 'cohort', 'variation')) {
					fireAnalyticEventOnceForMsr(
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'exposed',
								actionSubject: 'msrCjnExperiment',
								source: 'crossJoinNudges',
								attributes: {
									flagKey: 'twcg-437-cjn-msr-m1',
									value: 'variation',
								},
							},
						}),
					);
					setEnrolledInMsr();
					await runMsrNudges();
				} else if (expValEquals('twcg-437-cjn-msr-m1', 'cohort', 'control')) {
					fireAnalyticEventOnceForMsr(
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'exposed',
								actionSubject: 'msrCjnExperiment',
								source: 'crossJoinNudges',
								attributes: {
									flagKey: 'twcg-437-cjn-msr-m1',
									value: 'control',
								},
							},
						}),
					);
					setEnrolledInMsr();
					await execNudges();
				} else {
					await execNudges();
				}
			} else {
				await execNudgesWithLoom();
			}
		};

		const init = async () => {
			if (fg('puma-326-cjn-orchestrator-confluence')) {
				await orchestrateNudges();
			} else {
				// exec old implementation
				await execNudges();
			}
		};

		if (execNudge) {
			void init();
		}
		// Disabling as we only want this to execute based on `execNudge` or if `flags` gets updated.
		// This prevents edge cases where flags might be shown twice due to
		// Complex dependency arrays of children components/dependencies
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [execNudge, flags]);
};

export const CrossJoinNudgesWithFlags = withFlags<{ execNudge: boolean }>(
	({ flags, execNudge = false }) => {
		useCrossJoinNudges({ flags, execNudge });
		return null;
	},
);
